'use client'

import { Avatar, Button } from '@comp/material'
import { commentTypes } from '@lib/config'
import { formatDate } from '@lib/datetime'
import { fetchPut } from '@lib/fetch/client'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import clsx from 'clsx'
import { map, size } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { useAsyncFn, useBoolean, useList } from 'react-use'
import CommentInputContent from '../../input/content'

export default function CommentRow({
  data,
  newsId,
  topicId,
  commentType,
  isReply = false
}) {
  const ts = useTranslations('article')
  const [childrenComment, { push: addNewChildrenComment }] = useList(data.children_comment)
  const replyCount = size(childrenComment)

  // 点赞数量
  const [thumbNumber, setThumbNumber] = useState(data.thumb_number)
  // 是否已经点赞
  const [isThumbed, setIsThumbed] = useState(data.is_thumbed)
  // 评论点赞
  const [thumbState, handleThumb] = useAsyncFn(async () => {
    await fetchPut(`news/comment/${data.id}/thumb`)
    setThumbNumber((d) => d + 1)
    setIsThumbed(true)
  })

  // 是否显示回复框
  const [showReply, setShowReply] = useBoolean(false)

  return (
    <div className={clsx(isReply ? 'pt-pix-16' : 'border-b py-pix-20')}>
      <div className="flex gap-x-pix-12">
        <Avatar
          src={data.avatar}
          alt={data.user_nick_name}
          sx={{ width: 30, height: 30 }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex gap-x-pix-10 items-center">
            <span className="text-primary text-f.7">{data.user_id ? data.user_nick_name : ts('anonymous')}</span>
            {
              data.anonymous && (
                <span className="text-helper text-f.6">
                  {ts('from')}
                  {data.anonymous.location}
                </span>
              )
            }
            <span className="text-helper text-f.6">{formatDate(data.publish_at * 1000, 'YYYY/MM/DD HH:mm')}</span>
          </div>
          <div className="flex gap-x-pix-10 items-start mt-pix-20">
            <div className="text-c3b text-f.7 flex-1 min-w-0">
              {
                data.reply_comment && (
                  <>
                    <span>{ts('reply')}</span>
                    <span className="ml-pix-4 mr-pix-8 text-primary">
                      {data.reply_comment.user_id ? data.reply_comment.user_nick_name : ts('anonymous')}
                      :
                    </span>
                  </>
                )
              }
              <span>{data.body}</span>
            </div>
            <Button variant="text" className="text-f.7 p-0" onClick={handleThumb} disabled={thumbState.loading || isThumbed}>
              {
                isThumbed && (
                  <ThumbUpOffAltIcon className="text-primary mr-pix-6" />
                )
              }
              <span>
                {
                  thumbState.loading
                    ? ts('loading')
                    : (
                      <>
                        {ts('like')}
                        {` ${thumbNumber}`}
                      </>
                    )
                }
              </span>
            </Button>
          </div>
          {
            commentType !== commentTypes.close && !showReply && (
              <div className="mt-pix-8">
                <Button variant="text" className="text-f.7 p-0 min-w-0 inline-flex gap-x-pix-6" onClick={() => setShowReply(true)}>
                  <span>{ts('reply')}</span>
                  {
                    replyCount > 0 && (
                      <>
                        <i className="w-pix-4 h-pix-4 inline-block bg-primary rounded-full" />
                        <span className="inline-flex items-center">
                          {ts('replayCount', { count: replyCount })}
                        </span>
                      </>
                    )
                  }
                </Button>
              </div>
            )
          }
        </div>
      </div>
      {
        showReply && (
          <div className="bg-cf5 flex gap-x-pix-10 p-pix-10 pl-0">
            <div className="w-pix-30 h-pix-30" />
            <div className="flex-1 min-w-0">
              <div>
                <Button variant="text" className="text-f.7 px-0 min-w-0 inline-flex gap-x-pix-6" onClick={() => setShowReply(false)}>
                  <span>{ts('hide')}</span>
                </Button>
              </div>
              <CommentInputContent
                newsId={newsId}
                topicId={topicId}
                replyId={data.id}
                contentMinRows={4}
                commentType={commentType}
                onAdd={addNewChildrenComment}
              />
            </div>
          </div>
        )
      }
      {
        replyCount > 0 && (
          <div className="flex gap-x-pix-10">
            <div className="w-pix-30 h-pix-30" />
            <div className="flex-1 min-w-0">
              {
                map(childrenComment, (item) => (
                  <CommentRow data={item} key={item.id} newsId={newsId} topicId={topicId} isReply />
                ))
              }
            </div>
          </div>
        )
      }
    </div>
  )
}
